// Chooses the API implementation at startup.
// - VITE_MOCK=1 (demo build, `npm run dev:mock`): always the in-memory mock.
// - `npm run dev` without the flag: the real backend, unless GET /api/me fails (backend not
//   running), then the mock, with a small "Demo podatki" badge on screen.
// - production build (`npm run build`, no VITE_MOCK): always the real backend, never the mock.
//   MOCK_ALLOWED is a build-time constant there (false), so the bundler drops the dynamic
//   import below and no mock code or demo data ships in dist/ (check: `npm run check:nomock`).

import { isRevoked, type Api } from "./contract";
import { httpApi } from "./http";
import { notifyRevoked } from "../lib/clientState";

export { ApiError, isConflict, isRevoked } from "./contract";
export type { Api, SystemPatch } from "./contract";

const MOCK_BUILD = import.meta.env.VITE_MOCK === "1";
const MOCK_ALLOWED = MOCK_BUILD || import.meta.env.DEV;

let impl: Api = httpApi;
let mockOn = false;

export function isMock(): boolean {
  return mockOn;
}

export async function initApi(): Promise<void> {
  if (!MOCK_ALLOWED) return;
  const useMock = async () => {
    const { createMockApi } = await import("./mock");
    impl = createMockApi();
    mockOn = true;
  };
  if (MOCK_BUILD) {
    await useMock();
    return;
  }
  try {
    const res = await fetch("/api/me", { credentials: "include" });
    const type = res.headers.get("content-type") || "";
    if (!res.ok || !type.includes("json")) await useMock();
  } catch {
    await useMock();
  }
}

/** The active implementation. Import this everywhere.
 *  Every call goes through one check: a 403 {code: "revoked"} (pilot access removed) tells the
 *  session to drop the client state and show the "access removed" screen. */
export const api: Api = new Proxy({} as Api, {
  get(_t, key: string) {
    const v = (impl as unknown as Record<string, unknown>)[key];
    if (typeof v !== "function") return v;
    return (...args: unknown[]) => {
      const r = (v as (...a: unknown[]) => unknown)(...args);
      if (r instanceof Promise) {
        return r.catch((e: unknown) => {
          if (isRevoked(e)) notifyRevoked();
          throw e;
        });
      }
      return r;
    };
  },
});
